/**
 * Prototyper Facade (100% Shared)
 *
 * Wraps prototyper-related state and actions for WebContainer streaming:
 * publish, demo link metadata, and build-stream model selection.
 *
 * @module usePrototyperFacade
 * @category Facades/Shared
 */

import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  setIsFetchingPrototype,
  setPrototypeError,
  setCurrentPrototyper,
  updatePrototyperSlug,
  updatePrototyperDemoUrl,
  clearPrototyper,
  clearPrototypeError,
  selectPrototyperState,
  selectPrototypeGenerationModel,
  setPrototypeGenerationModel,
  setStreamDemoLinkId,
} from '../../../store/slices/prototyperSlice';
import { prototyperService } from '../../../api/services/prototyper.service';

export interface CurrentPrototyper {
  demo_url: string | null;
  prototyper_demo_link_id: number | null;
  slug: string | null;
  header: string | null;
}

export interface PrototyperFacade {
  prototypeError: string | null;
  currentPrototyper: CurrentPrototyper;
  cancelPrototype: () => void;
  clearError: () => void;
  updatePrototyperDemoLink: (id: number, slug?: string | null) => Promise<void>;
  publishToDemo: (
    projectId: string,
    conversationId: string,
    slug: string,
    snapshotId?: string,
  ) => Promise<void>;
  fetchPrototyperById: (id: string | number) => Promise<void>;
  setStreamDemoLinkId: (id: number) => void;
  prototypeGenerationModel: { modelProvider: string; modelChoice: string };
  setPrototypeGenerationModel: (model: { modelProvider: string; modelChoice: string }) => void;
}

export function usePrototyperFacade(
  _projectId?: string | null,
  activeConversationId?: string | null,
): PrototyperFacade {
  const dispatch = useAppDispatch();
  const prototyperState = useAppSelector(selectPrototyperState);
  const prototypeGenerationModel = useAppSelector(selectPrototypeGenerationModel);

  /**
   * Live pointer to the conversation currently on screen (plain assignment, not a ref
   * synced via effect, so it's already current by the time an in-flight request below
   * resolves). `currentPrototyper` in Redux is a single flat object with no per-chat key,
   * so without this guard a publish/fetch/update-demo-link request started in chat A that
   * resolves after the user has switched to chat B would still overwrite B's prototype
   * state with A's stale result.
   */
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;

  const setGenerationModel = useCallback(
    (model: { modelProvider: string; modelChoice: string }) => {
      dispatch(setPrototypeGenerationModel(model));
    },
    [dispatch],
  );

  const safePrototyperState = prototyperState || {
    currentPrototyper: {
      demo_url: null,
      prototyper_demo_link_id: null,
      slug: null,
      header: null,
    },
    prototypeError: null,
  };

  const cancelPrototype = useCallback(() => {
    dispatch(clearPrototyper());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(clearPrototypeError());
  }, [dispatch]);

  const updatePrototyperDemoLink = useCallback(
    async (id: number, slug?: string | null) => {
      const requestConversationId = activeConversationIdRef.current;
      try {
        await prototyperService.updatePrototyperDemoLink({ id, slug });
        // Skip if the user has since switched to a different conversation — this
        // dispatch would otherwise overwrite that conversation's prototype state.
        if (activeConversationIdRef.current !== requestConversationId) return;
        if (slug !== undefined) {
          dispatch(updatePrototyperSlug(slug));
        }
      } catch (error) {
        console.error('Error updating prototyper demo link:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update prototyper demo link';
        if (activeConversationIdRef.current === requestConversationId) {
          dispatch(setPrototypeError(errorMessage));
        }
        throw error;
      }
    },
    [dispatch],
  );

  const fetchPrototyperById = useCallback(
    async (id: string | number) => {
      const requestConversationId = activeConversationIdRef.current;
      try {
        dispatch(setIsFetchingPrototype(true));
        const response = await prototyperService.getPrototyperById(id);
        // Skip if the user has since switched to a different conversation — this
        // dispatch would otherwise overwrite that conversation's prototype state.
        if (activeConversationIdRef.current !== requestConversationId) return;
        // `response.id`/`response.prototyper_demo_link_id` come back as strings (bigint
        // column serialized to JSON) — coerce to a number so this matches the `number |
        // null` type `CurrentPrototyper.prototyper_demo_link_id` declares. Dispatching the
        // raw string here previously made every subsequent `numId !== prototyperDemoLinkId`
        // comparison against a fetched numeric id permanently true (123 !== "123"), which
        // sent callers of this facade (e.g. the snapshot-restore backfill effect in
        // ConversationPage) into an infinite refetch loop.
        const rawPrototyperId = response.id ?? response.prototyper_demo_link_id;
        const prototyperId = Number(rawPrototyperId);
        dispatch(
          setCurrentPrototyper({
            demo_url: response.demo_url || response.demoUrl || null,
            prototyper_demo_link_id: Number.isFinite(prototyperId) ? prototyperId : null,
            slug: response.slug || null,
            header: response.header || null,
          }),
        );
      } catch (error) {
        console.error('Error fetching prototyper:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch prototyper';
        if (activeConversationIdRef.current === requestConversationId) {
          dispatch(setPrototypeError(errorMessage));
        }
        throw error;
      } finally {
        // Always clear the loading flag, even if stale — it's a plain boolean spinner,
        // not per-conversation state, and skipping this would leave it stuck `true`.
        dispatch(setIsFetchingPrototype(false));
      }
    },
    [dispatch],
  );

  const setStreamDemoLinkIdCb = useCallback(
    (id: number) => {
      dispatch(setStreamDemoLinkId(id));
    },
    [dispatch],
  );

  const publishToDemo = useCallback(
    async (
      projectId: string,
      conversationId: string,
      slug: string,
      snapshotId?: string,
    ) => {
      try {
        const response = await prototyperService.publishPrototype(projectId, conversationId, {
          slug,
          snapshotId,
        });
        // Skip if the user has since switched away from `conversationId` — this dispatch
        // would otherwise overwrite the now-active conversation's prototype state.
        if (activeConversationIdRef.current !== conversationId) return;
        dispatch(updatePrototyperSlug(response.slug));
        dispatch(updatePrototyperDemoUrl(response.previewUrl));
        // The server always resolves (or auto-provisions) a `prototyper_demo_links` row for
        // this conversation before deploying — populate it here rather than waiting on the
        // live `demoLinkCreated` SSE event, which a resumed/restored build never replays.
        // Without this, `currentPrototypeId` stays null after a publish in this same session,
        // and Unpublish (which still goes through the id-keyed PATCH endpoint) fails with
        // "No prototype ID available" even though the prototype just published successfully.
        const demoLinkId = Number(response.prototyperDemoLinkId);
        if (Number.isFinite(demoLinkId)) {
          dispatch(setStreamDemoLinkId(demoLinkId));
        }
      } catch (error) {
        console.error('Error publishing prototype:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to publish prototype';
        if (activeConversationIdRef.current === conversationId) {
          dispatch(setPrototypeError(errorMessage));
        }
        throw error;
      }
    },
    [dispatch],
  );

  return {
    prototypeError: safePrototyperState.prototypeError,
    currentPrototyper: safePrototyperState.currentPrototyper,
    cancelPrototype,
    clearError,
    updatePrototyperDemoLink,
    fetchPrototyperById,
    publishToDemo,
    setStreamDemoLinkId: setStreamDemoLinkIdCb,
    prototypeGenerationModel,
    setPrototypeGenerationModel: setGenerationModel,
  };
}
